import type { RefObject } from 'react';
import type { PropertyGroupResponse } from '@/shared/api';
import { ArchiveObjectDialog } from './ArchiveObjectDialog';
import { MergeObjectDialog } from './MergeObjectDialog';
import { ObjectStructureChooser } from './ObjectStructureChooser';
import { SplitObjectDialog } from './SplitObjectDialog';
import { useObjectStructure } from './useObjectStructure';

interface Props {
  group: PropertyGroupResponse;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  returnFocusTo?: RefObject<HTMLElement | null>;
}

/**
 * Auswahl und Dialoge der Objektstruktur. Immer nur ein Dialog ist offen: die Auswahl schließt
 * sich, bevor der gewählte Weg aufgeht.
 */
export function ObjectStructureControl({ group, open, onOpenChange, returnFocusTo }: Props) {
  const structure = useObjectStructure(group);
  const close = () => structure.setOpen(null);

  return (
    <>
      <ObjectStructureChooser
        group={group}
        open={open}
        onOpenChange={onOpenChange}
        returnFocusTo={returnFocusTo}
        onChoose={(action) => {
          onOpenChange(false);
          structure.setOpen(action);
        }}
      />
      {structure.open === 'merge' && (
        <MergeObjectDialog
          source={group}
          all={structure.groups}
          failed={structure.groupsFailed}
          pending={structure.merge.isPending}
          onClose={close}
          onConfirm={(targetGroupId) => structure.merge.mutate(targetGroupId)}
        />
      )}
      {structure.open === 'split' && (
        <SplitObjectDialog
          source={group}
          pending={structure.split.isPending}
          onClose={close}
          onConfirm={(body) => structure.split.mutate(body)}
        />
      )}
      {structure.open === 'archive' && (
        <ArchiveObjectDialog
          group={group}
          pending={structure.archive.isPending}
          onClose={close}
          onConfirm={() => structure.archive.mutate()}
        />
      )}
    </>
  );
}
